import React from 'react';
import { useTranslation } from 'react-i18next';
import { ListPlus, Loader2 } from 'lucide-react';
import { SiFacebook, SiInstagram, SiTiktok, SiYoutube } from 'react-icons/si';
import { useHasFeature } from '@/lib/useHasFeature';
import { SOCIAL_PUBLISHING_FEATURE } from '@/lib/publishing/socialPublishingFeature';
import { useSocialPostingPlatforms } from '@/hooks/use-social-posting-platforms';
import type { PublishPlatform, PublishUiStatus } from '@/lib/publishing/types';

export interface ClipPostActionsInput {
  clipId: string;
  /** Rendered and not expired: gates the post menu (queue/TikTok/IG/FB/YT). */
  canPublish: boolean;
  compact?: boolean;
  onSendToQueue?: (clipId: string) => void;
  onSendToTikTok?: (clipId: string) => void;
  onPublishToYouTube?: (clipId: string) => void;
  onPublishToInstagram?: (clipId: string) => void;
  /** Shown only when the viewer may post to Facebook (`useSocialPostingPlatforms`). */
  onPublishToFacebook?: (clipId: string) => void;
  tiktokPublishStatus?: PublishUiStatus;
  tiktokPublishFailReason?: string | null;
  youtubePublishStatus?: PublishUiStatus;
  youtubePublishFailReason?: string | null;
  instagramPublishStatus?: PublishUiStatus;
  instagramPublishFailReason?: string | null;
  facebookPublishStatus?: PublishUiStatus;
  facebookPublishFailReason?: string | null;
}

export interface PostAction {
  key: string;
  testId: string;
  label: string;
  icon: React.ReactNode;
  disabled: boolean;
  busy: boolean;
  onSelect: () => void;
}

export interface LiveRegion {
  platform: PublishPlatform;
  status: PublishUiStatus;
  failReason: string | null;
}

export interface ClipPostActions {
  /** Live regions and (with `actions`) the Postar trigger render. */
  showPostControls: boolean;
  actions: PostAction[];
  anyPublishing: boolean;
  liveRegions: LiveRegion[];
  compact: boolean;
}

/** The Postar menu's gates and items, shared by every clip-card shape so the
 *  card can tell whether Postar shows before laying out its buttons. */
export function useClipPostActions(input: ClipPostActionsInput): ClipPostActions {
  const { t } = useTranslation();
  const hasSocialPublishing = useHasFeature(SOCIAL_PUBLISHING_FEATURE);
  // Facebook posting has its own gate on top of social_publishing: until Meta
  // approves it, only some viewers may post, so the handler counts only for them.
  const canPostToFacebook = useSocialPostingPlatforms().includes('facebook');
  const {
    clipId,
    canPublish,
    compact = false,
    onSendToQueue,
    onSendToTikTok,
    onPublishToYouTube,
    onPublishToInstagram,
    tiktokPublishStatus,
    youtubePublishStatus,
    instagramPublishStatus,
    facebookPublishStatus,
  } = input;
  const publishToFacebook = canPostToFacebook ? input.onPublishToFacebook : undefined;

  const supportsTikTok = !!onSendToTikTok || tiktokPublishStatus !== undefined;
  const supportsYouTube = !!onPublishToYouTube || youtubePublishStatus !== undefined;
  const supportsInstagram = !!onPublishToInstagram || instagramPublishStatus !== undefined;
  const supportsFacebook = !!publishToFacebook || facebookPublishStatus !== undefined;
  const supportsQueue = !!onSendToQueue;
  const iconClassName = compact ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const anyPublishing = tiktokPublishStatus === 'publishing'
    || youtubePublishStatus === 'publishing'
    || instagramPublishStatus === 'publishing'
    || facebookPublishStatus === 'publishing';

  const platformAction = (
    key: string,
    testId: string,
    platform: PublishPlatform,
    status: PublishUiStatus | undefined,
    Icon: React.ComponentType<{ className?: string }>,
    handler: ((clipId: string) => void) | undefined,
  ): PostAction | null => (handler ? {
    key,
    testId,
    label: t(`publishing.platforms.${platform}`),
    icon: status === 'publishing'
      ? <Loader2 className={`${iconClassName} animate-spin`} />
      : <Icon className={iconClassName} />,
    disabled: status === 'publishing',
    busy: status === 'publishing',
    onSelect: () => handler(clipId),
  } : null);

  const actions = [
    onSendToQueue ? {
      key: 'queue',
      testId: 'send-to-queue',
      label: t('publishQueues.send.action'),
      icon: <ListPlus className={iconClassName} />,
      disabled: false,
      busy: false,
      onSelect: () => onSendToQueue(clipId),
    } : null,
    platformAction('tiktok', 'send-to-tiktok', 'tiktok', tiktokPublishStatus, SiTiktok, onSendToTikTok),
    platformAction('instagram', 'publish-to-instagram', 'instagram', instagramPublishStatus, SiInstagram, onPublishToInstagram),
    platformAction('facebook', 'publish-to-facebook', 'facebook', facebookPublishStatus, SiFacebook, publishToFacebook),
    platformAction('youtube', 'publish-to-youtube', 'youtube', youtubePublishStatus, SiYoutube, onPublishToYouTube),
  ].filter((action): action is PostAction => action != null);

  // The post menu (queue/TikTok/Instagram/Facebook/YouTube) requires
  // social_publishing on top of having a handler.
  const showPostControls = canPublish && hasSocialPublishing
    && (supportsQueue || supportsTikTok || supportsYouTube || supportsInstagram || supportsFacebook);

  const liveRegions: LiveRegion[] = [];
  if (supportsTikTok) {
    liveRegions.push({ platform: 'tiktok', status: tiktokPublishStatus ?? 'none', failReason: input.tiktokPublishFailReason ?? null });
  }
  if (supportsInstagram) {
    liveRegions.push({ platform: 'instagram', status: instagramPublishStatus ?? 'none', failReason: input.instagramPublishFailReason ?? null });
  }
  if (supportsFacebook) {
    liveRegions.push({ platform: 'facebook', status: facebookPublishStatus ?? 'none', failReason: input.facebookPublishFailReason ?? null });
  }
  if (supportsYouTube) {
    liveRegions.push({ platform: 'youtube', status: youtubePublishStatus ?? 'none', failReason: input.youtubePublishFailReason ?? null });
  }

  return { showPostControls, actions, anyPublishing, liveRegions, compact };
}
